/**
 * Five metric cards displayed in a single row below the hero.
 * Per spec §4. All values reflect the last 7 days (rolling window),
 * scoped to the current subject.
 *
 * Cards:
 *   1. Active last 7 days       (N / total)
 *   2. Avg questions / student
 *   3. Students with active gaps  (red emphasis if > 0)
 *   4. Gaps cleared this week     (green)
 *   5. Engagement distribution    (3-segment horizontal bar)
 */

function MetricCard({ label, value, sub, emphasis, children }) {
  const valueColor = emphasis === 'green' ? 'text-teacher-green'
    : emphasis === 'red' ? 'text-teacher-red'
    : 'text-teacher-text-primary';
  return (
    <div className="bg-white border border-teacher-border rounded-xl px-4 py-3.5">
      <div className="text-[10px] uppercase tracking-wider text-teacher-text-secondary mb-1.5 font-medium">
        {label}
      </div>
      {value !== undefined && (
        <div className={`text-2xl font-medium leading-none mb-1 ${valueColor}`}>
          {value}
        </div>
      )}
      {children}
      {sub && (
        <div className="text-[11px] text-teacher-text-secondary mt-1">{sub}</div>
      )}
    </div>
  );
}

function EngagementBar({ daily, mid, occasional }) {
  const total = daily + mid + occasional;
  if (total === 0) {
    return (
      <div className="text-xs text-teacher-text-tertiary mt-1">No active students</div>
    );
  }
  const dPct = (daily / total) * 100;
  const mPct = (mid / total) * 100;
  const oPct = (occasional / total) * 100;
  return (
    <>
      <div className="flex items-center text-[13px] font-medium mb-1.5">
        <span className="text-teacher-text-secondary">Daily · 3-4x · Occasional</span>
      </div>
      <div className="flex gap-[2px] h-1.5 rounded overflow-hidden">
        <div className="bg-teacher-green" style={{ width: `${dPct}%` }} />
        <div className="bg-teacher-blue" style={{ width: `${mPct}%` }} />
        <div className="bg-teacher-text-tertiary" style={{ width: `${oPct}%` }} />
      </div>
      <div className="text-[11px] text-teacher-text-secondary mt-1">
        {daily} · {mid} · {occasional} students
      </div>
    </>
  );
}

export default function MetricsRow({ metrics, totalStudents }) {
  const m = metrics || {};
  const activeNum = m.active_last_7d || 0;
  const total = totalStudents || 0;
  const activePct = total > 0 ? Math.round((activeNum / total) * 100) : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      <MetricCard
        label="Active last 7 days"
        emphasis={activeNum > 0 ? 'green' : undefined}
        value={
          <>
            {activeNum}
            <span className="text-sm font-normal text-teacher-text-secondary">/{total}</span>
          </>
        }
        sub={total > 0 ? `${activePct}% of batch` : ''}
      />
      <MetricCard
        label="Avg questions / student"
        value={m.avg_questions_per_student || 0}
        sub="last 7 days"
      />
      <MetricCard
        label="Students with active gaps"
        emphasis={(m.students_with_active_gaps || 0) > 0 ? 'red' : undefined}
        value={m.students_with_active_gaps || 0}
        sub="open thinking gaps"
      />
      <MetricCard
        label="Gaps cleared this week"
        emphasis="green"
        value={m.gaps_cleared_last_7d || 0}
        sub="Atlas verified"
      />
      <MetricCard label="Engagement distribution">
        <EngagementBar
          daily={m.engagement_distribution?.daily || 0}
          mid={m.engagement_distribution?.mid || 0}
          occasional={m.engagement_distribution?.occasional || 0}
        />
      </MetricCard>
    </div>
  );
}
